import { TextWithUnderline } from "by/gamefactory/cityversetycoon/scenes/game/components/TextWithUnderline";
import { NewsItemWithAvatar } from "./NewsItemWithAvatar";
import { FederatedPointerEvent, IDestroyOptions, Text, Texture, utils } from "pixi.js";
import { UserProfileResourcePackage } from "../../../userprofile/resources/UserProfileResourcePackage";
import { INewsItemOptions } from "./NewsItem";
import { NewsTextStyle } from "../styles/NewsTextStyle";
import { LatestNewsResourcePackage } from "../../resources/LatestNewsResourcePackage";
import { Event } from "event-system";
import { Chain } from "thirdweb";
import { Web3Config } from "../../../../../../web3/config/Web3Config";

export enum NewsItemPurchaseType {
    GOLDEN_WHEEL = "GOLDEN_WHEEL",
    BUILDING = "BUILDING",
    DICE_ROLLS = "DICE_ROLLS"
}

type NewsItemPurchaseViewDescription = {
    descriptionText: string;
    buttonText: string;
}

class NewsItemPurchasingConfig {
    public static readonly VIEW_CONFIG = new Map<NewsItemPurchaseType, NewsItemPurchaseViewDescription>([
        [NewsItemPurchaseType.GOLDEN_WHEEL, {
            descriptionText: "YOUR TRANSACTION TO\nPURCHASE A GOLDEN WHEEL HAS\nBEEN COMPLETED SUCCESSFULLY.",
            buttonText: "CLICK HERE TO SPIN THE WHEEL"
        }],
        [NewsItemPurchaseType.BUILDING, {
            descriptionText: "YOUR TRANSACTION TO\nPURCHASE A BUILDING HAS BEEN\nCOMPLETED SUCCESSFULLY.",
            buttonText: "CLICK HERE TO EXPLORE IT"
        }],
        [NewsItemPurchaseType.DICE_ROLLS, {
            descriptionText: "YOUR TRANSACTION TO PURCHASE\nA PACKAGE OF DICE ROLLS HAS\nBEEN COMPLETED SUCCESSFULLY.",
            buttonText: "CLICK HERE TO EXPLORE IT"
        }],
    ])
}

export class NewsItemPurchasing extends NewsItemWithAvatar {
    private readonly type: NewsItemPurchaseType;
    private readonly viewDescription: NewsItemPurchaseViewDescription;
    private readonly web3Config: Web3Config;

    private descriptionText: Text;
    private buttonText: TextWithUnderline;

    public readonly onShowGoldenWheel: Event<void>;
    public readonly onSeeTransactionDetails: Event<Chain>;

    public constructor(
        id: string,
        newsResources: LatestNewsResourcePackage,
        userProfileResources: UserProfileResourcePackage,
        options: NewsItemPurchasingOptions,
        web3Config: Web3Config
    ) {
        super(
            id,
            newsResources,
            userProfileResources,
            newsResources.assets_views_latest_news_news_tooltip_red_png,
            newsResources.assets_views_latest_news_news_purchasing_png,
            options.playerAvatarTexture
        );
        this.type = options.type;
        this.viewDescription = NewsItemPurchasingConfig.VIEW_CONFIG.get(options.type);
        this.web3Config = web3Config;
        this.onShowGoldenWheel = new Event();
        this.onSeeTransactionDetails = new Event();
        this.build(options);
        this.layout();
        this.buttonText.on(utils.isMobile.apple.device ? "pointerup" : "pointerdown", this.onButtonClickHandle, this);
    }

    private build(options: NewsItemPurchasingOptions): void {
        this.descriptionText = new Text(this.viewDescription.descriptionText, new NewsTextStyle());
        this.buttonText = new TextWithUnderline(this.viewDescription.buttonText, new ButtonTextStyle(), 0, -12);
        this.buttonText.eventMode = 'static';
        this.addChild(this.descriptionText, this.buttonText);
        this.avatar.updateTexture(options.playerAvatarTexture);
    }

    private layout(): void {
        this.descriptionText.position.set(411, 30);
        this.buttonText.position.set(411, 160);
    }

    private async onButtonClickHandle(_event: FederatedPointerEvent): Promise<void> {
        switch (this.type) {
            case NewsItemPurchaseType.GOLDEN_WHEEL: {
                this.onShowGoldenWheel.emit();
                break;
            }
            case NewsItemPurchaseType.BUILDING: {
                this.onSeeTransactionDetails.emit(this.web3Config.targetChain);
                break;
            }
            case NewsItemPurchaseType.DICE_ROLLS: {
                this.onSeeTransactionDetails.emit(this.web3Config.targetChain);
                break;
            }
        }
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.buttonText.off(utils.isMobile.apple.device ? "pointerup" : "pointerdown", this.onButtonClickHandle, this);
        super.destroy(options);
    }
}

export class NewsItemPurchasingOptions implements INewsItemOptions {
    public readonly playerAvatarTexture: Texture;
    public readonly type: NewsItemPurchaseType;

    public constructor(playerAvatarTexture: Texture, type: NewsItemPurchaseType) {
        this.playerAvatarTexture = playerAvatarTexture;
        this.type = type;
    }
}

class ButtonTextStyle extends NewsTextStyle {
    public constructor() {
        super();
        this.fill = '81CAFF';
    }
}